import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider as JotaiProvider, useSetAtom } from 'jotai';
import { Suspense, lazy, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import {
  ImportCartsModal,
  Layout,
  LoadingCartFullPage,
  RequireAuth,
} from './components';
import { useConvertLocalCartsToCookieCarts } from './hooks';
import { AuthProvider } from './providers';
import { analyticsConsentAtom } from './store';
import { ScrollToAnchor, localUrl } from './utils';
import { rememberInAppPath } from './utils/returnTo';
import { applyConsentToServices } from './utils/cookieConsentServices';
import * as Views from './Views';

// Lazy-loaded so the developer docs (and their MDX / syntax-highlighting deps)
// stay out of the main bundle and off every other route.
const Developer = lazy(() =>
  import('./Views/Developer').then((m) => ({ default: m.Developer })),
);
const DeveloperAppsPage = lazy(() =>
  import('./Views/DeveloperAppsPage').then((m) => ({ default: m.DeveloperAppsPage })),
);
const Docs = lazy(() =>
  import('./Views/Docs').then((m) => ({ default: m.Docs })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export default function App() {
  const { t, i18n } = useTranslation();
  const setAnalyticsConsent = useSetAtom(analyticsConsentAtom);

  const lng = i18n.resolvedLanguage;
  const location = useLocation();

  useConvertLocalCartsToCookieCarts();

  useEffect(() => {
    rememberInAppPath(location.pathname, location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;
    const privacyHref = localUrl('/privacy');

    CookieConsent.run({
      autoShow: false,
      mode: 'opt-out',
      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {
          enabled: true,
          services: {
            'Google Tag Manager': {
              label: 'Google Tag Manager',
            },
            LiveSession: {
              label: 'LiveSession',
            },
            PostHog: {
              label: 'PostHog',
            },
          },
        },
      },
      guiOptions: {
        consentModal: {
          layout: 'box inline',
          position: 'bottom center',
          equalWeightButtons: true,
        },
        preferencesModal: {
          layout: 'box',
          equalWeightButtons: true,
        },
      },
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'We use cookies',
              description:
                `We use cookies and similar technologies to help personalize content and analyze our traffic. You can choose to accept all, only necessary, or manage your preferences. See our <a href="${privacyHref}" class="underline hover:opacity-80">Privacy Policy</a> for more.`,
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Necessary only',
              showPreferencesBtn: 'Manage Cookies',
              footer:
                `<a href="${privacyHref}" class="underline hover:opacity-80">Privacy Policy</a>`,
            },
            preferencesModal: {
              title: 'Cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Necessary only',
              savePreferencesBtn: 'Save selection',
              closeIconLabel: 'Close',
              sections: [
                {
                  title: 'Strictly necessary',
                  description:
                    'These cookies are required for the site to work and cannot be turned off.',
                  linkedCategory: 'necessary',
                },
                {
                  title: 'Analytics',
                  description:
                    'These cookies help us understand how visitors use the site so we can improve it. All data is anonymized.',
                  linkedCategory: 'analytics',
                },
                {
                  title: 'More information',
                  description:
                    `For questions about our use of cookies and your choices, see our <a href="${privacyHref}" class="underline hover:opacity-80">Privacy Policy</a>.`,
                },
              ],
            },
          },
        },
      },
      onConsent: () => {
        applyConsentToServices(setAnalyticsConsent);
      },
      onChange: () => {
        applyConsentToServices(setAnalyticsConsent);
      },
    }).then(() => {
      if (!cancelled) {
        applyConsentToServices(setAnalyticsConsent);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [setAnalyticsConsent]);

  return (
    <HelmetProvider>
      <Helmet prioritizeSeoTags htmlAttributes={{ lang: lng }} />
      <Suspense fallback={<LoadingCartFullPage />}>
        <QueryClientProvider client={queryClient}>
          <JotaiProvider>
            <AuthProvider>
              <main className='flex flex-col'>
                <Routes>
                  {/* we don't use navbar or footer here */}
                  <Route path='/embed/:cartId?' element={<Views.GetEmbed />} />
                  {/*
              Login has no Footer and a different Header
              component than the other routes
            */}
                  <Route
                    path='/login'
                    element={
                      <>
                        <Helmet>
                          <title>
                            {t('common.amazingly_simple_list_sharing_sac')}
                          </title>
                        </Helmet>
                        <Views.Login />
                      </>
                    }
                  />
                  <Route
                    path='/receive-mobile/:cartId?'
                    element={<Views.ReceiveMobile />}
                  />
                  <Route path='/' element={<Layout />}>
                    <Route path='/get/:cartId?' element={<Views.Get />} />
                    <Route path='/make/:cartId?' element={<Views.Make />} />
                    <Route
                      path='/receive/:cartId?'
                      element={<Views.Receive />}
                    />
                    <Route
                      path='/review/:cartId?/:response?'
                      element={<Views.Review />}
                    />
                    <Route
                      path='/plus/cart-history'
                      element={
                        <>
                          <Helmet>
                            <title>Share-A-Cart+</title>
                          </Helmet>
                          <RequireAuth>
                            <Views.CartHistory />
                          </RequireAuth>
                        </>
                      }
                    />
                    <Route
                      path='/plus/my-account'
                      element={
                        <>
                          <Helmet>
                            <title>
                              {t('common.amazingly_simple_list_sharing_sac')}
                            </title>
                          </Helmet>
                          <RequireAuth>
                            <Views.MyAccount />
                          </RequireAuth>
                        </>
                      }
                    />
                    <Route path='/plus/upgrade' element={<Views.Upgrade />} />
                    <Route
                      path='/plus/upgrade-success'
                      element={<Views.UpgradeSuccess />}
                    />
                    <Route
                      path='/plus/subscribe'
                      element={
                        <RequireAuth>
                          <Views.Subscribe />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path='/plus/manage-subscription'
                      element={
                        <RequireAuth>
                          <Views.ManageSub />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path='/plus/your-org'
                      element={
                        <RequireAuth requireOrg>
                          <Views.OrganizationSettings />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path='/plus/organization-settings'
                      element={<Navigate to='/plus/your-org' replace />}
                    />
                    <Route
                      path='/plus/cart-analytics'
                      element={
                        <RequireAuth requireOrgAdmin>
                          <Navigate to='/plus/your-org?tab=analytics' replace />
                        </RequireAuth>
                      }
                    />

                    {/* Not yet implemented */}
                    <Route
                      path='/plus/profile'
                      element={
                        <RequireAuth>
                          <Views.ComingSoon />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path='/plus/use-cases'
                      element={<Views.ComingSoon />}
                    />
                    <Route
                      path='/plus/coming-soon'
                      element={<Views.ComingSoon />}
                    />

                    <Route path='/deals/:store/:deal' element={<Views.DealDetail />} />
                    <Route path='/deals/:store' element={<Views.Deals />} />
                    <Route path='/deals' element={<Views.Deals />} />

                    <Route path='/developers' element={<Developer />} />
                    <Route path='/developers/apps' element={<DeveloperAppsPage />} />
                    <Route path='/developers/docs/*' element={<Docs />} />

                    <Route
                      path='/changelog'
                      element={
                        <>
                          <Helmet>
                            <title>{t('changelog.seo.title')}</title>
                            <meta
                              name='description'
                              content={t('changelog.seo.meta_desc')}
                            />
                          </Helmet>
                          <Views.Changelog />
                        </>
                      }
                    />

                    <Route
                      path='/safari-ios-installation-helper'
                      element={<Views.SafariIosHelper />}
                    />

                    {/* Redirect to /plus/cart-history */}
                    <Route path='/cart-history' element={<Views.Home />} />
                  </Route>
                </Routes>

                <ImportCartsModal />

                <ScrollToAnchor />

                <Toaster
                  position='top-right'
                  toastOptions={{
                    classNames: {
                      error: 'bg-errorRedLight text-errorRedDark',
                      success: 'bg-[#7CFDD7] text-[#207259]',
                      warning: 'bg-[#FFE9AF] text-[#78683D]',
                      info: 'bg-skyBlue text-[#2E4B93]',
                      toast: 'bg-skyBlue rounded-lg w-full',
                      title:
                        'text-[#2E4B93] font-primary text-sm font-semibold',
                      description: 'text-[#2E4B93] font-secondary text-xs',
                    },
                  }}
                />
              </main>
            </AuthProvider>
          </JotaiProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </Suspense>
    </HelmetProvider>
  );
}
